import React, { useState } from "react";
import { Box, TextField, Button, Typography, IconButton, Alert } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { Contact, ContactStatus } from "../../../interfaces/DTO/crm/contact";

import { CreatePublicAction, PublicLinkActionType } from "../../../interfaces/DTO/PublicAction";
import { publicActionService } from "../../../services/PublicActionService";
import { PreAdviceRequestComponent } from "../../Consulting/PreAdviceRequestComponent";
import { useCreateContact } from "../../../hooks/crm/useContacts";

enum ChatStep {
  ASK_NAME = "ASK_NAME",
  ASK_PHONE = "ASK_PHONE",
  ASK_EMAIL = "ASK_EMAIL",
  ASK_EMAIL_CORRECTION = "ASK_EMAIL_CORRECTION",
  ASK_ID = "ASK_ID",
  CONFIRM = "CONFIRM",
  ASK_SEND_CONSENT_LINK = "ASK_SEND_CONSENT_LINK",
  SHOW_LINK = "SHOW_LINK",
  ASK_SEND_METHOD = "ASK_SEND_METHOD",
  COMPLETED = "COMPLETED",
}

export const ContactCreateChat: React.FC<{
  onSuccess?: (contact: Contact) => void;
  onCancel?: () => void;
}> = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState<ChatStep>(ChatStep.ASK_NAME);
  const [messages, setMessages] = useState<
    { text: string; isUser: boolean; isError?: boolean }[]
  >([{ text: "מה שם הלקוח (פרטי ומשפחה)?", isUser: false }]);
  const [inputValue, setInputValue] = useState("");
  const [contactData, setContactData] = useState<Partial<Contact>>({});
  const createContact = useCreateContact();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestedEmail, setSuggestedEmail] = useState<string>("");
  const [createdContact, setCreatedContact] = useState<Contact | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInput = (value: string) => {
    setMessages((prev) => [...prev, { text: value, isUser: true }]);
    setInputValue("");

    switch (step) {
      case ChatStep.ASK_NAME:
        handleNameInput(value);
        break;
      case ChatStep.ASK_PHONE:
        handlePhoneInput(value);
        break;
      case ChatStep.ASK_EMAIL:
        handleEmailInput(value);
        break;
      case ChatStep.ASK_EMAIL_CORRECTION:
        handleEmailCorrectionInput(value);
        break;
      case ChatStep.ASK_ID:
        handleIdInput(value);
        break;
      case ChatStep.ASK_SEND_CONSENT_LINK:
        handleSendConsentLinkResponse(value);
        break;
    }
  };

  const handleNameInput = (value: string) => {
    const nameParts = value.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    setContactData((prev) => ({
      ...prev,
      first_name: firstName,
      last_name: lastName,
    }));

    setMessages((prev) => [
      ...prev,
      { text: "מה מספר הטלפון?", isUser: false },
    ]);
    setStep(ChatStep.ASK_PHONE);
  };

  const handlePhoneInput = (value: string) => {
    setContactData((prev) => ({ ...prev, phone: value }));
    setMessages((prev) => [
      ...prev,
      { text: "מה האימייל?", isUser: false },
    ]);
    setStep(ChatStep.ASK_EMAIL);
  };

  const handleEmailInput = (value: string) => {
    if (!validateEmail(value)) {
      if (!value.includes("@")) {
        const suggested = `${value}@gmail.com`;
        setSuggestedEmail(suggested);
        setMessages((prev) => [
          ...prev,
          {
            text: `האימייל לא תקין. האם התכוונת ל-${suggested}?`,
            isUser: false,
            isError: true,
          },
        ]);
        setStep(ChatStep.ASK_EMAIL_CORRECTION);
        return;
      } else {
        setMessages((prev) => [
          ...prev,
          { text: "האימייל לא תקין. אנא הזן אימייל חוקי.", isUser: false, isError: true },
        ]);
        return;
      }
    }

    setContactData((prev) => ({ ...prev, email: value }));
    setMessages((prev) => [
      ...prev,
      { text: "מה תעודת הזהות? (אופציונלי - אפשר להשאיר ריק)", isUser: false },
    ]);
    setStep(ChatStep.ASK_ID);
  };

  const handleEmailCorrectionInput = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === "כן" || normalizedValue === "yes") {
      // Use the suggested email
      setContactData((prev) => ({ ...prev, email: suggestedEmail }));
      setMessages((prev) => [
        ...prev,
        { text: "מה תעודת הזהות? (אופציונלי - אפשר להשאיר ריק)", isUser: false },
      ]);
      setStep(ChatStep.ASK_ID);
    } else if (normalizedValue === "לא" || normalizedValue === "no") {
      // Ask for email again
      setMessages((prev) => [
        ...prev,
        { text: "בסדר, אנא הזן את האימייל הנכון:", isUser: false },
      ]);
      setStep(ChatStep.ASK_EMAIL);
    } else {
      // Treat any other input as a new email attempt
      handleEmailInput(value);
    }
  };

  const handleIdInput = (value: string) => {
    if (value.trim()) {
      setContactData((prev) => ({ ...prev, israeli_id: value.trim() }));
    }
    setStep(ChatStep.CONFIRM);
    setMessages((prev) => [
      ...prev,
      { text: "מצוין! נא לאשר את הפרטים:", isUser: false },
    ]);
  };

  const handleSkipId = () => {
    setStep(ChatStep.CONFIRM);
    setMessages((prev) => [
      ...prev,
      { text: "דילגתי", isUser: true },
      { text: "מצוין! נא לאשר את הפרטים:", isUser: false },
    ]);
  };

  const handleSendConsentLinkResponse = async (value: string) => {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === "כן" || normalizedValue === "yes") {
      // יצירת לינק
      await generateConsentLink();
    } else {
      // לא רוצה לשלוח לינק - סיום
      setMessages((prev) => [
        ...prev,
        { text: "בסדר, תהליך יצירת הלקוח הושלם בהצלחה!", isUser: false },
      ]);
      setStep(ChatStep.COMPLETED);
    }
  };

  const generateConsentLink = async () => {
    if (!createdContact) return;

    setGeneratingLink(true);
    setMessages((prev) => [
      ...prev,
      { text: "יוצר קישור לטופס הסכמה...", isUser: false },
    ]);

    try {
      const dto = CreatePublicAction.defaultEmpty();
      dto.contact_id = createdContact.id;
      dto.action_type = PublicLinkActionType.VIEW_P_CONSENT;
      dto.meta_data = null;

      await publicActionService.createToken(dto);

      setMessages((prev) => [
        ...prev,
        { text: "הקישור נוצר בהצלחה!", isUser: false },
      ]);

      setStep(ChatStep.SHOW_LINK);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        {
          text: err?.response?.data?.detail || "שגיאה ביצירת הקישור. נסה שוב.",
          isUser: false,
          isError: true,
        },
      ]);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSubmit = async () => {
  setIsSubmitting(true);

  const newContact: Partial<Contact> = {
    ...contactData,
    status: ContactStatus.NEW,
  };

  createContact.mutate(newContact as Omit<Contact, "id">, {
    onSuccess: (created) => {
      setCreatedContact(created);

      setMessages((prev) => [
        ...prev,
        { text: "הלקוח נוצר בהצלחה!", isUser: false },
        { text: "האם תרצה לשלוח ללקוח לינק לטפסי חתימה לטרום ייעוץ?", isUser: false },
      ]);

      setStep(ChatStep.ASK_SEND_CONSENT_LINK);

      onSuccess?.(created);
      setIsSubmitting(false);
    },

    onError: (error: any) => {
      console.error("Error creating contact:", error);

      setMessages((prev) => [
        ...prev,
        { text: "שגיאה ביצירת הלקוח. אנא נסה שוב.", isUser: false, isError: true },
      ]);

      setIsSubmitting(false);
    },
  });
};


  return (
    <Box
      sx={{
        padding: { xs: 1, sm: 4 },
        maxWidth: { xs: "100%", sm: 600 },
        margin: "0 auto",
        minHeight: 400,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#e5ddd5",
        borderRadius: 4,
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0icGF0dGVybiIgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMCw1MCBMIDUwLDAgTCAxMDAsNTAgTCA1MCwxMDAgWiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZDlkOWQ5IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4yIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIi8+PC9zdmc+')",
      }}
    >
      <Typography variant="h5" align="center" gutterBottom sx={{ color: "#075e54", fontWeight: 600 }}>
        יצירת לקוח חדש
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginBottom: 2,
          padding: 2,
          borderRadius: 2,
          minHeight: 300,
          backgroundColor: "rgba(255,255,255,0.3)",
        }}
      >
        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: msg.isUser ? "flex-end" : "flex-start",
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                padding: 1.5,
                maxWidth: "70%",
                backgroundColor: msg.isError
                  ? "#ffebee"
                  : msg.isUser
                  ? "#dcf8c6"
                  : "#ffffff",
                color: msg.isError ? "#c62828" : "inherit",
                borderRadius: 2,
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  [msg.isUser ? "right" : "left"]: -8,
                  width: 0,
                  height: 0,
                  borderStyle: "solid",
                  borderWidth: msg.isUser ? "0 10px 10px 0" : "0 0 10px 10px",
                  borderColor: msg.isError
                    ? msg.isUser
                      ? "transparent #ffebee transparent transparent"
                      : "transparent transparent transparent #ffebee"
                    : msg.isUser
                    ? "transparent #dcf8c6 transparent transparent"
                    : "transparent transparent transparent #ffffff",
                },
              }}
            >
              <Typography variant="body1">{msg.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {step === ChatStep.ASK_EMAIL_CORRECTION || step === ChatStep.ASK_SEND_CONSENT_LINK ? (
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleInput("כן")}
            sx={{ minWidth: 100 }}
            disabled={generatingLink}
          >
            כן
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleInput("לא")}
            sx={{ minWidth: 100 }}
            disabled={generatingLink}
          >
            {step === ChatStep.ASK_SEND_CONSENT_LINK ? "לא עכשיו" : "לא"}
          </Button>
        </Box>
      ) : step === ChatStep.SHOW_LINK ? (
        createdContact && (
          <PreAdviceRequestComponent
            contactId={createdContact.id}
            contactName={`${createdContact.first_name} ${createdContact.last_name}`}
          />
        )
      ) : step === ChatStep.COMPLETED ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
          <Alert severity="success">התהליך הושלם בהצלחה!</Alert>
          {onCancel && (
            <Button variant="contained" onClick={onCancel}>
              סגור
            </Button>
          )}
        </Box>
      ) : step === ChatStep.CONFIRM ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, direction: "rtl" }}>
          <TextField
            label="שם פרטי"
            value={contactData.first_name || ""}
            onChange={(e) =>
              setContactData((prev) => ({ ...prev, first_name: e.target.value }))
            }
            fullWidth
            inputProps={{ style: { direction: "rtl" } }}
          />
          <TextField
            label="שם משפחה"
            value={contactData.last_name || ""}
            onChange={(e) =>
              setContactData((prev) => ({ ...prev, last_name: e.target.value }))
            }
            fullWidth
            inputProps={{ style: { direction: "rtl" } }}
          />
          <TextField
            label="טלפון"
            value={contactData.phone || ""}
            onChange={(e) =>
              setContactData((prev) => ({ ...prev, phone: e.target.value }))
            }
            fullWidth
            inputProps={{ style: { direction: "rtl" } }}
          />
          <TextField
            label="אימייל"
            value={contactData.email || ""}
            onChange={(e) =>
              setContactData((prev) => ({ ...prev, email: e.target.value }))
            }
            fullWidth
            inputProps={{ style: { direction: "rtl" } }}
          />
          {contactData.israeli_id && (
            <TextField
              label="ת.ז"
              value={contactData.israeli_id || ""}
              onChange={(e) =>
                setContactData((prev) => ({ ...prev, israeli_id: e.target.value }))
              }
              fullWidth
              inputProps={{ style: { direction: "rtl" } }}
            />
          )}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}>
            <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
              ביטול
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "יוצר..." : "צור לקוח חדש"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, direction: "rtl", alignItems: "center" }}>
          <IconButton
            color="primary"
            onClick={() => inputValue.trim() && handleInput(inputValue.trim())}
            disabled={!inputValue.trim()}
            sx={{
              backgroundColor: "#25d366",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#20ba5a",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            <SendIcon />
          </IconButton>
          {step === ChatStep.ASK_ID && (
            <Button variant="outlined" onClick={handleSkipId} sx={{ borderRadius: 2 }}>
              דלג
            </Button>
          )}
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && inputValue.trim()) {
                handleInput(inputValue.trim());
              }
            }}
            placeholder="הקלד תשובה..."
            fullWidth
            autoFocus
            inputProps={{ style: { direction: "rtl" } }}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
